import { ChGridManagerColumnDrag } from "./ch-grid-manager-column-drag";
import { ChGridManagerColumns } from "./ch-grid-manager-columns";
import { ChGridManagerSelection } from "./ch-grid-manager-selection";
import { ChGridManagerRowDrag } from "./ch-grid-manager-row-drag";
import { ChGridManagerRowActions } from "./ch-grid-manager-row-actions";
import { adoptGlobalStyleSheet } from "../../deprecated-components/style/ch-global-stylesheet";
import { ChGridManagerColumnResize } from "./ch-grid-manager-column-resize";
var StyleRule;
(function (StyleRule) {
    StyleRule[StyleRule["BASE_LAYER"] = 0] = "BASE_LAYER";
    StyleRule[StyleRule["COLUMNS_WIDTH"] = 1] = "COLUMNS_WIDTH";
})(StyleRule || (StyleRule = {}));
export class ChGridManager {
    styleSheet = new CSSStyleSheet();
    gridLayoutElement;
    columnDragManager;
    columnResizeManager;
    rowDragManager;
    grid;
    selection;
    columns;
    rowActions;
    constructor(grid) {
        this.grid = grid;
        this.styleSheet.insertRule(`:host {}`, StyleRule.BASE_LAYER);
        this.styleSheet.insertRule(".main {}", StyleRule.COLUMNS_WIDTH);
        this.grid.shadowRoot.adoptedStyleSheets.push(this.styleSheet);
        adoptGlobalStyleSheet(this.grid.shadowRoot.adoptedStyleSheets);
        this.columns = new ChGridManagerColumns(this);
        this.selection = new ChGridManagerSelection(this);
        this.rowActions = new ChGridManagerRowActions(this);
    }
    get gridLayout() {
        return this.gridLayoutElement;
    }
    componentDidLoad(gridLayout) {
        this.gridLayoutElement = gridLayout;
    }
    getColumns() {
        return this.columns.getColumns();
    }
    getColumnsWidth() {
        return getComputedStyle(this.gridLayout).gridTemplateColumns.split(" ");
    }
    getColumnsetHeight() {
        const gridColumnsHeight = getComputedStyle(this.gridLayout).gridTemplateRows.split(" ");
        return parseInt(gridColumnsHeight[0]) || 0;
    }
    getFirstColumn() {
        return this.columns.getColumnsFirstLast().columnFirst;
    }
    getFirstRow() {
        return this.grid.querySelector("ch-grid-row");
    }
    getScrollOffsetTop() {
        return this.gridLayout.offsetTop + this.getColumnsetHeight();
    }
    getScrollOffsetLeft() {
        return this.columns.getColumns(true).reduce((offsetRight, column) => {
            return column.freeze === "start" && !column.hidden
                ? offsetRight + column.offsetWidth
                : offsetRight;
        }, 0);
    }
    getScrollOffsetRight() {
        return this.columns.getColumns(true).reduce((offsetRight, column) => {
            return column.freeze === "end" && !column.hidden
                ? offsetRight + column.offsetWidth
                : offsetRight;
        }, 0);
    }
    getPreviousRow(current) {
        const rows = this.getRows();
        const i = rows.indexOf(current);
        return rows
            .slice(0, i)
            .reverse()
            .find(row => row.isVisible());
    }
    getNextRow(current) {
        const rows = this.getRows();
        const i = rows.indexOf(current);
        return rows.slice(i + 1).find(row => row.isVisible());
    }
    getLastRow() {
        const rows = this.getRows();
        return rows.reverse().find(row => row.isVisible());
    }
    getPreviousColumn(column) {
        return this.columns.getColumns().reduce((previous, current) => {
            return current.order < column.order &&
                !current.hidden &&
                (!previous || current.order > previous.order)
                ? current
                : previous;
        }, null);
    }
    getNextColumn(column) {
        return this.columns.getColumns().reduce((previous, current) => {
            return current.order > column.order &&
                !current.hidden &&
                (!previous || current.order < previous.order)
                ? current
                : previous;
        }, null);
    }
    getPreviousCell(current) {
        const previousColumn = this.getPreviousColumn(current.column);
        if (previousColumn) {
            return current.row.querySelector(`:scope > ch-grid-cell:nth-of-type(${previousColumn.physicalOrder})`);
        }
    }
    getNextCell(current) {
        const nextColumn = this.getNextColumn(current.column);
        if (nextColumn) {
            return current.row.querySelector(`:scope > ch-grid-cell:nth-of-type(${nextColumn.physicalOrder})`);
        }
    }
    getGridRowIndex(row) {
        return Array.prototype.indexOf.call(this.grid.querySelectorAll("ch-grid-row, ch-grid-rowset-legend"), row);
    }
    getRowsetRowIndex(row) {
        return Array.prototype.indexOf.call(row.parentElement.children, row);
    }
    getRowHeight(row) {
        const gridRowsHeight = getComputedStyle(this.gridLayout).gridTemplateRows.split(" ");
        const rowIndex = this.getGridRowIndex(row) + 1;
        return parseInt(gridRowsHeight[rowIndex]) || 0;
    }
    getRowsPerPage() {
        const gridHeight = this.gridLayout.clientHeight;
        const columnsHeight = this.getColumnsetHeight();
        const rowHeight = this.getRowHeight(this.getFirstRow());
        return Math.floor((gridHeight - columnsHeight) / rowHeight);
    }
    getRow(rowId) {
        return this.grid.querySelector(`ch-grid-row[rowid="${rowId}"]`);
    }
    getRows(state = "all") {
        const rows = Array.from(this.grid.querySelectorAll(`ch-grid-row`));
        if (state === "visible") {
            return rows.filter(row => row.isVisible());
        }
        return rows;
    }
    getRowsRange(start, end) {
        const rows = this.getRows();
        const indexStart = rows.indexOf(start);
        const indexEnd = rows.indexOf(end);
        return rows
            .slice(Math.min(indexStart, indexEnd), Math.max(indexStart, indexEnd) + 1)
            .filter(row => row.isVisible());
    }
    getRowEventTarget(eventInfo) {
        return eventInfo
            .composedPath()
            .find((target) => target.tagName === "CH-GRID-ROW");
    }
    isRowActionsEventTarget(eventInfo) {
        return (eventInfo
            .composedPath()
            .find((target) => target.tagName === "CH-GRID-ROW-ACTIONS") != null);
    }
    getCell(cellId, rowId, columnId) {
        if (cellId) {
            return this.grid.querySelector(`ch-grid-cell[cellid="${cellId}"]`);
        }
        if (rowId && columnId) {
            const row = this.getRow(rowId);
            const column = this.columns.getColumn(columnId);
            if (row && column) {
                return row.getCell(column);
            }
        }
    }
    getCellEventTarget(eventInfo) {
        return eventInfo
            .composedPath()
            .find((target) => target.tagName === "CH-GRID-CELL");
    }
    columnDragStart(columnId) {
        this.columnDragManager = new ChGridManagerColumnDrag(columnId, this.columns.getColumns(), this.isRTLDirection());
    }
    columnDragging(position) {
        return this.columnDragManager.dragging(position);
    }
    columnDragEnd() {
        this.columnDragManager.dragEnd();
        this.columnDragManager = null;
    }
    columnResizeStart(columnId) {
        this.columnResizeManager = new ChGridManagerColumnResize(this, columnId, this.isRTLDirection());
    }
    columnResizing(deltaWidth) {
        this.columnResizeManager.resizing(deltaWidth);
    }
    columnResizeEnd() {
        this.columnResizeManager.resizeEnd();
        this.columnResizeManager = null;
    }
    rowDragStart(row) {
        this.rowDragManager = new ChGridManagerRowDrag(this);
        this.rowDragManager.dragStart(row);
    }
    getGridStyle() {
        return {
            display: "grid",
            ...this.getGridTemplateColumns(),
            ...this.getRowBoxSimulationStyle(),
            ...this.getDragTransitionStyle(),
            ...this.getColumnsStyle()
        };
    }
    setBaseLayer(value) {
        this.styleSheet.deleteRule(StyleRule.BASE_LAYER);
        this.styleSheet.insertRule(`:host { --ch-grid-base-layer: ${value}; }`, StyleRule.BASE_LAYER);
    }
    setColumnWidthVariables(columnsWidth) {
        this.styleSheet.deleteRule(StyleRule.COLUMNS_WIDTH);
        this.styleSheet.insertRule(`.main { ${columnsWidth
            .map((columnWidth, columnIndex) => `--ch-grid-column-${columnIndex + 1}-width:${columnWidth}px;`)
            .join("\n")} }`, StyleRule.COLUMNS_WIDTH);
    }
    ensureRowVisible(row) {
        let node = row.parentElement.closest("ch-grid-row, ch-grid-rowset");
        const { columnFirst } = this.columns.getColumnsFirstLast();
        while (node) {
            node.collapsed = false;
            node = node.parentElement.closest("ch-grid-row, ch-grid-rowset");
        }
        if (row.children[columnFirst.physicalOrder]) {
            this.ensureVisible(row.children[columnFirst.physicalOrder]);
        }
    }
    ensureCellVisible(cell) {
        let node = cell.closest("ch-grid-row, ch-grid-rowset");
        while (!cell.isVisible() && node) {
            node.collapsed = false;
            node = node.parentElement.closest("ch-grid-row, ch-grid-rowset");
        }
        if (!cell.isVisible()) {
            cell.column.hidden = false;
        }
        this.ensureVisible(cell);
    }
    ensureVisible(cell) {
        const isColumnFreeze = ["start", "end"].includes(cell.column.freeze);
        const scroll = this.gridLayout;
        const scrollOffsetTop = this.getScrollOffsetTop();
        const scrollOffsetLeft = this.getScrollOffsetLeft();
        const scrollOffsetRight = this.getScrollOffsetRight();
        if (scroll.scrollTop + scrollOffsetTop > cell.offsetTop) {
            scroll.scrollBy({
                top: (scroll.scrollTop - cell.offsetTop + scrollOffsetTop) * -1
            });
        }
        else if (scroll.scrollTop + scroll.offsetHeight <
            cell.offsetTop + cell.offsetHeight) {
            scroll.scrollBy({
                top: cell.offsetTop +
                    cell.offsetHeight -
                    (scroll.scrollTop + scroll.offsetHeight)
            });
        }
        else if (scroll.scrollLeft + scrollOffsetLeft > cell.offsetLeft &&
            !isColumnFreeze) {
            scroll.scrollBy({
                left: (scroll.scrollLeft - cell.offsetLeft + scrollOffsetLeft) * -1
            });
        }
        else if (scroll.scrollLeft + scroll.clientWidth - scrollOffsetRight <
            cell.offsetLeft + cell.offsetWidth &&
            !isColumnFreeze) {
            scroll.scrollBy({
                left: cell.offsetLeft +
                    cell.offsetWidth -
                    (scroll.scrollLeft + scroll.clientWidth - scrollOffsetRight)
            });
        }
    }
    getGridTemplateColumns() {
        return {
            "grid-template-columns": this.columns
                .getColumns()
                .map(column => `var(--ch-grid-column-${column.physicalOrder}-size)`)
                .join(" ")
        };
    }
    getRowBoxSimulationStyle() {
        const { columnFirst, columnLast } = this.columnDragManager
            ? this.columnDragManager.getColumnsFirstLast()
            : this.columns.getColumnsFirstLast();
        if (!columnFirst || !columnLast) {
            return null;
        }
        return {
            [`--ch-grid-column-${columnFirst.physicalOrder}-margin-start`]: "var(--ch-grid-fallback, inherit)",
            [`--ch-grid-column-${columnFirst.physicalOrder}-border-start`]: "var(--ch-grid-fallback, inherit)",
            [`--ch-grid-column-${columnFirst.physicalOrder}-padding-start`]: "var(--ch-grid-fallback, inherit)",
            [`--ch-grid-column-${columnLast.physicalOrder}-margin-end`]: "var(--ch-grid-fallback, inherit)",
            [`--ch-grid-column-${columnLast.physicalOrder}-border-end`]: "var(--ch-grid-fallback, inherit)",
            [`--ch-grid-column-${columnLast.physicalOrder}-padding-end`]: "var(--ch-grid-fallback, inherit)"
        };
    }
    getDragTransitionStyle() {
        return {
            "--column-drag-transition-duration": this.columnDragManager ? ".2s" : "0s"
        };
    }
    getColumnsStyle() {
        return this.columns.getColumns().reduce((style, column) => {
            return {
                ...style,
                ...this.getColumnStyle(column)
            };
        }, {});
    }
    getColumnStyle(column) {
        return {
            ...this.getColumnSizeStyle(column),
            ...this.getColumnOrderStyle(column),
            ...this.getColumnDisplayStyle(column),
            ...this.getColumnFreezeStyle(column),
            ...this.getColumnDraggingStyle(column),
            ...this.getColumnIndentStyle(column)
        };
    }
    getColumnSizeStyle(column) {
        return {
            [`--ch-grid-column-${column.order}-size`]: column.hidden
                ? "0px"
                : column.size
        };
    }
    getColumnOrderStyle(column) {
        return {
            [`--ch-grid-column-${column.physicalOrder}-position`]: column.order.toString()
        };
    }
    getColumnFreezeStyle(column) {
        switch (column.freeze) {
            case "start":
                return this.getColumnFreezeStartStyle(column);
            case "end":
                return this.getColumnFreezeEndStyle(column);
        }
    }
    getColumnFreezeStartStyle(column) {
        const calcItems = ["0px"];
        for (let i = 1; i < column.order; i++) {
            calcItems.push(`var(--ch-grid-column-${i}-width)`);
        }
        return {
            [`--ch-grid-column-${column.physicalOrder}-left-freeze`]: `calc(${calcItems.join(" + ")})`,
            [`--ch-grid-column-${column.physicalOrder}-z-index-freeze`]: "var(--ch-grid-column-freeze-layer)"
        };
    }
    getColumnFreezeEndStyle(column) {
        const calcItems = ["0px"];
        for (let i = this.columns.getColumns().length; i > column.order; i--) {
            calcItems.push(`var(--ch-grid-column-${i}-width)`);
        }
        return {
            [`--ch-grid-column-${column.physicalOrder}-right-freeze`]: `calc(${calcItems.join(" + ")})`,
            [`--ch-grid-column-${column.physicalOrder}-z-index-freeze`]: "var(--ch-grid-column-freeze-layer)"
        };
    }
    getColumnDisplayStyle(column) {
        return column.hidden
            ? {
                [`--ch-grid-column-${column.physicalOrder}-display`]: "none"
            }
            : null;
    }
    getColumnDraggingStyle(column) {
        return this.columnDragManager
            ? this.columnDragManager.getColumnStyle(column)
            : null;
    }
    getColumnIndentStyle(column) {
        return {
            [`--ch-grid-column-${column.physicalOrder}-content`]: column.order === 1 ? "''" : "none"
        };
    }
    isRTLDirection() {
        return getComputedStyle(this.grid).direction === "rtl";
    }
}
